import { AnalyticsType } from "@src/appV2/Analytics/constants";
import { getLocation } from "@src/appV2/redesign/utils/routes";

import { type WorkplaceMenuPriviliges } from "../priviligesProviders/WorkplaceMenuPriviligesProvider";
import { type NavigationItem, type NavigationMenu, type NavigationSection } from "./types";
import { getRouteAndSelected } from "./utils";

interface AnalyticsConfig {
  type: AnalyticsType;
  embedId: string;
}

export function getWorkplaceNavigationMenu(
  workplaceMenuPriviliges: WorkplaceMenuPriviliges,
  currentPath: string,
  analyticsConfig: AnalyticsConfig[] = []
): NavigationMenu {
  const items: Array<NavigationItem | NavigationSection> = [];

  if (workplaceMenuPriviliges.workplaceOnboarding) {
    items.push({
      label: "Getting started",
      iconType: "hybrid-view",
      ...getRouteAndSelected("facilityOnboarding", currentPath),
    });
  }

  if (workplaceMenuPriviliges.dailyView) {
    items.push({
      label: "Daily View",
      iconType: "calendar-day",
      ...getRouteAndSelected("dailyView", currentPath),
    });
  }

  if (workplaceMenuPriviliges.calendar) {
    items.push({
      label: "Calendar",
      iconType: "calendar",
      ...getRouteAndSelected("facilityDashboard", currentPath),
    });
  }

  if (workplaceMenuPriviliges.chat) {
    items.push({
      label: "Chat",
      iconType: "message",
      ...getRouteAndSelected("facilityChat", currentPath),
    });
  }

  if (workplaceMenuPriviliges.professionalsSection) {
    const sectionItems: NavigationItem[] = [];

    if (workplaceMenuPriviliges.professionalsSection.myProfessionals) {
      sectionItems.push({
        label: "My Professionals",
        iconType: "folder-person",
        ...getRouteAndSelected("workplaceWorkers", currentPath),
      });
    }
    if (workplaceMenuPriviliges.professionalsSection.browseProfessionals) {
      sectionItems.push({
        label: "Browse Professionals",
        iconType: "people",
        ...getRouteAndSelected("workersDirectory", currentPath),
      });
    }

    items.push({ label: "Professionals", items: sectionItems });
  }

  if (workplaceMenuPriviliges.permanentHiringSection) {
    const sectionItems: NavigationItem[] = [];

    if (workplaceMenuPriviliges.permanentHiringSection.placementsLandingPage) {
      sectionItems.push({
        label: "Landing Page",
        iconType: "ribbon",
        ...getRouteAndSelected("jobsLandingPage", currentPath),
      });
    }
    if (workplaceMenuPriviliges.permanentHiringSection.applicationAndJobs) {
      sectionItems.push({
        label: "Application & Jobs",
        iconType: "briefcase",
        ...getRouteAndSelected("manageJobs", currentPath),
      });
    }
    if (workplaceMenuPriviliges.permanentHiringSection.browseTalentPool) {
      sectionItems.push({
        label: "Browse Talent Pool",
        iconType: "person-superstar",
        ...getRouteAndSelected("workplaceHireProfessionals", currentPath),
      });
    }
    if (workplaceMenuPriviliges.permanentHiringSection.interviewAvailability) {
      sectionItems.push({
        label: "Interview Availability",
        iconType: "person-male-formal",
        ...getRouteAndSelected("interviewSettings", currentPath),
      });
    }

    items.push({ label: "Permanent Hiring", items: sectionItems });
  }

  if (workplaceMenuPriviliges.charting) {
    const sectionItems: NavigationItem[] = [];

    sectionItems.push({
      label: "Charting",
      iconType: "chart-line-increase",
      ...getRouteAndSelected("workplaceCharting", currentPath),
    });

    items.push({ label: "Charting", items: sectionItems });
  }

  if (workplaceMenuPriviliges.analyticsSection) {
    const sectionItems: NavigationItem[] = [];
    const workplaceAnalyticsPath = "/workplace/analytics";

    const workplaceAnalyticsEmbedId = analyticsConfig.find(
      (config) => config.type === AnalyticsType.WORKPLACE_FLEX
    )?.embedId;

    const corporateAnalyticsEmbedId = analyticsConfig.find(
      (config) => config.type === AnalyticsType.CORPORATE_FLEX
    )?.embedId;

    if (workplaceMenuPriviliges.analyticsSection.workplaceAnalytics && workplaceAnalyticsEmbedId) {
      sectionItems.push({
        label: "Workplace Analytics",
        iconType: "microscope",
        routeTo: getLocation("workplaceAnalytics", {
          queryParams: { embedId: workplaceAnalyticsEmbedId },
        }),
        isSelected: currentPath.includes(workplaceAnalyticsPath),
      });
    }

    if (workplaceMenuPriviliges.analyticsSection.corporateAnalytics && corporateAnalyticsEmbedId) {
      sectionItems.push({
        label: "Corporate Analytics",
        iconType: "binoculars",
        routeTo: getLocation("workplaceAnalytics", {
          queryParams: { embedId: corporateAnalyticsEmbedId },
        }),
        isSelected: currentPath.includes(workplaceAnalyticsPath),
      });
    }

    if (sectionItems.length > 0) {
      items.push({ label: "Analytics", items: sectionItems });
    }
  }

  if (workplaceMenuPriviliges.liveWorkerETA) {
    const sectionItems: NavigationItem[] = [];

    sectionItems.push({
      label: "Live Worker ETA",
      iconType: "coordinates-person",
      ...getRouteAndSelected("facilityLiveWorkerETA", currentPath),
    });

    items.push({ label: "Live Worker ETA", items: sectionItems });
  }

  if (workplaceMenuPriviliges.managementSection) {
    const sectionItems: NavigationItem[] = [];

    if (workplaceMenuPriviliges.managementSection.myAccount) {
      sectionItems.push({
        label: "My Account",
        iconType: "person-male",
        ...getRouteAndSelected("myAccount", currentPath),
      });
    }

    if (workplaceMenuPriviliges.managementSection.teamMembers) {
      sectionItems.push({
        label: "Team Members",
        iconType: "group",
        ...getRouteAndSelected("workplaceUsers", currentPath),
      });
    }

    if (workplaceMenuPriviliges.managementSection.settings) {
      sectionItems.push({
        label: "Settings",
        iconType: "cogwheel",
        ...getRouteAndSelected("facilitySettings", currentPath),
      });
    }

    if (workplaceMenuPriviliges.managementSection.timeclockingSettings) {
      sectionItems.push({
        label: "Timeclocking Settings",
        iconType: "cogwheel",
        ...getRouteAndSelected("facilityTimeclockSettings", currentPath),
      });
    }

    items.push({ label: "Management", items: sectionItems });
  }

  if (workplaceMenuPriviliges.billingSection) {
    const sectionItems: NavigationItem[] = [];

    if (workplaceMenuPriviliges.billingSection.upcomingCharges) {
      sectionItems.push({
        label: "Upcoming Charges",
        iconType: "dollar",
        ...getRouteAndSelected("facilityInvoiceUpcomingCharges", currentPath),
      });
    }

    if (workplaceMenuPriviliges.billingSection.invoiceHistory) {
      sectionItems.push({
        label: "Invoice History",
        iconType: "money-dollar",
        ...getRouteAndSelected("facilityInvoiceHistory", currentPath),
      });
    }

    if (workplaceMenuPriviliges.billingSection.contractTerms) {
      sectionItems.push({
        label: "Contract Terms",
        iconType: "notepad-alt",
        ...getRouteAndSelected("facilityInvoiceContractTerms", currentPath),
      });
    }

    items.push({ label: "Billing", items: sectionItems });
  }

  if (workplaceMenuPriviliges.helpCenter) {
    const sectionItems: NavigationItem[] = [];

    sectionItems.push({
      label: "Help Center",
      iconType: "question-circle",
      ...getRouteAndSelected("helpCenter", currentPath),
    });

    items.push({ label: "Help Center", items: sectionItems });
  }

  if (workplaceMenuPriviliges.referAndEarn) {
    const sectionItems: NavigationItem[] = [];

    sectionItems.push({
      label: "Refer & Earn",
      iconType: "palm-dollar",
      ...getRouteAndSelected("workplaceReferAndEarn", currentPath),
    });

    items.push({ label: "Refer & Earn", items: sectionItems });
  }

  return { items };
}
